import { Injectable, signal, computed } from '@angular/core'

export interface CartItem {
  productId: string
  name: string
  image: string
  price: number
  mrp?: number
  storeUsername: string
  storeName: string
  storePhone?: string
  storeWhatsapp?: string
  quantity: number
}

export interface StoreGroup {
  storeUsername: string
  storeName: string
  storePhone?: string
  storeWhatsapp?: string
  items: CartItem[]
  subtotal: number
}

@Injectable({ providedIn: 'root' })
export class CartService {
  private readonly STORAGE_KEY = 'isl_cart'

  private _items = signal<CartItem[]>(this.loadFromStorage())
  private _isOpen = signal(false)

  readonly items = this._items.asReadonly()
  readonly isOpen = this._isOpen.asReadonly()
  readonly count = computed(() => this._items().reduce((sum, item) => sum + item.quantity, 0))
  readonly total = computed(() => this._items().reduce((sum, item) => sum + item.price * item.quantity, 0))

  private loadFromStorage(): CartItem[] {
    try {
      const raw = localStorage.getItem(this.STORAGE_KEY)
      return raw ? JSON.parse(raw) : []
    } catch {
      return []
    }
  }

  private persist() {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this._items()))
  }

  open() { this._isOpen.set(true) }
  close() { this._isOpen.set(false) }

  addItem(item: Omit<CartItem, 'quantity'>) {
    const current = this._items()
    const existing = current.find(i => i.productId === item.productId)
    if (existing) {
      this._items.set(current.map(i =>
        i.productId === item.productId ? { ...i, quantity: i.quantity + 1 } : i
      ))
    } else {
      this._items.set([...current, { ...item, quantity: 1 }])
    }
    this.persist()
    this._isOpen.set(true)
  }

  removeItem(productId: string) {
    this._items.set(this._items().filter(i => i.productId !== productId))
    this.persist()
  }

  updateQuantity(productId: string, quantity: number) {
    if (quantity <= 0) { this.removeItem(productId); return }
    this._items.set(this._items().map(i =>
      i.productId === productId ? { ...i, quantity } : i
    ))
    this.persist()
  }

  clear() {
    this._items.set([])
    this.persist()
  }

  isInCart(productId: string): boolean {
    return this._items().some(i => i.productId === productId)
  }

  storeGroups(): StoreGroup[] {
    const map = new Map<string, StoreGroup>()
    for (const item of this._items()) {
      if (!map.has(item.storeUsername)) {
        map.set(item.storeUsername, {
          storeUsername: item.storeUsername,
          storeName: item.storeName,
          storePhone: item.storePhone,
          storeWhatsapp: item.storeWhatsapp,
          items: [],
          subtotal: 0
        })
      }
      const group = map.get(item.storeUsername)!
      group.items.push(item)
      group.subtotal += item.price * item.quantity
    }
    return Array.from(map.values())
  }

  whatsappLink(group: StoreGroup): string {
    const raw = (group.storeWhatsapp || group.storePhone || '').replace(/\D/g, '')
    const lines = group.items.map(i => `• ${i.name} x${i.quantity} — ₹${i.price * i.quantity}`)
    const text = `Hi! I'd like to order:\n\n${lines.join('\n')}\n\nTotal: ₹${group.subtotal}`
    const encoded = encodeURIComponent(text)
    if (raw) {
      const number = raw.startsWith('91') ? raw : `91${raw}`
      return `https://wa.me/${number}?text=${encoded}`
    }
    return `https://ig.me/m/${group.storeUsername}?text=${encoded}`
  }
}
